import { IPlayerVehicleListItem } from '~/shared';

import { createSlice } from '../wrappers';

type VehicleListName = 'work' | 'spawned' | 'nonSpawned';

const initialState = {
	selectedId: null as null | string,
	selectedIndex: 0,
	selectedList: 'spawned' as VehicleListName,
	vehicles: [] as IPlayerVehicleListItem[],
	spawnedVehicles: [] as IPlayerVehicleListItem[],
	nonSpawnedVehicles: [] as IPlayerVehicleListItem[],
	workVehicles: [] as IPlayerVehicleListItem[],
};

const getListByName = (
	state: typeof initialState,
	name: VehicleListName,
): IPlayerVehicleListItem[] => {
	switch (name) {
		case 'work':
			return state.workVehicles;
		case 'spawned':
			return state.spawnedVehicles;
		case 'nonSpawned':
			return state.nonSpawnedVehicles;
	}
};

const getOrderedLists = (state: typeof initialState) =>
	(['work', 'spawned', 'nonSpawned'] as const)
		.map((name) => ({ name, list: getListByName(state, name) }))
		.filter((entry) => entry.list.length > 0);

const selectFirstAvailable = (state: typeof initialState) => {
	const ordered = getOrderedLists(state);
	if (ordered.length === 0) {
		state.selectedId = null;
		state.selectedIndex = 0;
		return;
	}

	const first = ordered[0];
	state.selectedList = first.name;
	state.selectedIndex = 0;
	state.selectedId = first.list[0].id;
};

const vehicleListSlice = createSlice({
	name: 'vehicleList',
	initialState,
	reducers: {
		setSelectedId: (state, action) => {
			const { entryId, index, list } = action.payload;
			state.selectedId = entryId;
			state.selectedIndex = index;
			state.selectedList = list;
		},
		syncVehicleState: (state, action) => {
			const {
				vehicles,
				workVehicles = [],
				tabIndex,
			} = action.payload as {
				vehicles: IPlayerVehicleListItem[];
				workVehicles?: IPlayerVehicleListItem[];
				tabIndex: number;
			};

			state.vehicles = vehicles;
			state.workVehicles = workVehicles;

			const workIds = new Set(workVehicles.map((v) => v.id));
			const workNetIds = new Set(workVehicles.map((v) => v.vehicleNetId));
			const ownedWithoutWork = vehicles.filter(
				(v) =>
					!workIds.has(v.id) &&
					(v.vehicleNetId === undefined || !workNetIds.has(v.vehicleNetId)),
			);

			let visibleVehicles: IPlayerVehicleListItem[];
			switch (tabIndex) {
				case 1:
					visibleVehicles = ownedWithoutWork.filter(
						(v: IPlayerVehicleListItem) => v.isFavorite || v.isSpawned,
					);
					break;
				default:
					visibleVehicles = ownedWithoutWork;
					break;
			}

			const spawned: IPlayerVehicleListItem[] = [];
			const nonSpawned: IPlayerVehicleListItem[] = [];
			visibleVehicles.forEach((v: IPlayerVehicleListItem) =>
				v.isSpawned ? spawned.push(v) : nonSpawned.push(v),
			);

			state.spawnedVehicles = spawned;
			state.nonSpawnedVehicles = nonSpawned;

			if (state.selectedId !== null) {
				const workIndex = workVehicles.findIndex(
					(v) => v.id === state.selectedId,
				);
				if (workIndex !== -1) {
					state.selectedList = 'work';
					state.selectedIndex = workIndex;
					return;
				}

				const spawnedIndex = spawned.findIndex(
					(v) => v.id === state.selectedId,
				);
				if (spawnedIndex !== -1) {
					state.selectedList = 'spawned';
					state.selectedIndex = spawnedIndex;
					return;
				}

				const nonSpawnedIndex = nonSpawned.findIndex(
					(v) => v.id === state.selectedId,
				);
				if (nonSpawnedIndex !== -1) {
					state.selectedList = 'nonSpawned';
					state.selectedIndex = nonSpawnedIndex;
					return;
				}
			}

			selectFirstAvailable(state);
		},
		selectPrev: (state) => {
			const ordered = getOrderedLists(state);
			if (ordered.length === 0) return;

			if (state.selectedId === null) {
				selectFirstAvailable(state);
				return;
			}

			const currentIdx = ordered.findIndex(
				(entry) => entry.name === state.selectedList,
			);

			if (state.selectedIndex > 0) {
				const currentList = getListByName(state, state.selectedList);
				state.selectedIndex -= 1;
				state.selectedId = currentList[state.selectedIndex].id;
				return;
			}

			if (currentIdx > 0) {
				const prev = ordered[currentIdx - 1];
				const newIndex = prev.list.length - 1;
				state.selectedList = prev.name;
				state.selectedIndex = newIndex;
				state.selectedId = prev.list[newIndex].id;
			}
		},
		selectNext: (state) => {
			const ordered = getOrderedLists(state);
			if (ordered.length === 0) return;

			if (state.selectedId === null) {
				selectFirstAvailable(state);
				return;
			}

			const currentIdx = ordered.findIndex(
				(entry) => entry.name === state.selectedList,
			);
			if (currentIdx === -1) {
				selectFirstAvailable(state);
				return;
			}

			const current = ordered[currentIdx];
			if (state.selectedIndex < current.list.length - 1) {
				state.selectedIndex += 1;
				state.selectedId = current.list[state.selectedIndex].id;
				return;
			}

			if (currentIdx < ordered.length - 1) {
				const next = ordered[currentIdx + 1];
				state.selectedList = next.name;
				state.selectedIndex = 0;
				state.selectedId = next.list[0].id;
			}
		},
		resetSelection: (state) => {
			selectFirstAvailable(state);
		},
	},
});

export const {
	setSelectedId,
	syncVehicleState,
	selectPrev,
	selectNext,
	resetSelection,
} = vehicleListSlice.actions;

export default vehicleListSlice.reducer;
